import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertCircle } from "lucide-react";
import AlpacaPanel from "@/components/AlpacaPanel";
import IBKRPanel from "@/components/IBKRPanel";
import TradeStationPanel from "@/components/TradeStationPanel";

const CAP_LABELS = {
  market_data: "Market data",
  paper_trading: "Paper trading",
  live_trading: "Live trading",
  cloud_auth: "Cloud auth",
  local_gateway_required: "Local gateway required",
};

export default function Broker() {
  const [providers, setProviders] = useState([]);
  const [active, setActive] = useState("alpaca");

  useEffect(() => {
    api.get("/brokers").then(({ data }) => setProviders(data)).catch(() => setProviders([]));
  }, []);

  return (
    <div className="space-y-8" data-testid="broker-root">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Brokers</p>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-2">Real broker connections</h1>
        <p className="text-gray-500 mt-2 max-w-2xl">
          Route paper trades to a real market simulator with your own broker keys. Alpaca works end-to-end today; IBKR
          requires a local gateway and is stubbed until we ship the OAuth Web API integration.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2" data-testid="broker-tabs">
        {providers.map((p) => {
          const enabled = p.live_orders_enabled;
          return (
            <button
              key={p.provider}
              onClick={() => setActive(p.provider)}
              data-testid={`broker-tab-${p.provider}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                active === p.provider
                  ? "bg-brand text-white"
                  : "bg-gray-100 text-gray-600 hover:text-brand-deep"
              }`}
            >
              {enabled ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
              {p.label}
              {!enabled && (
                <span className="text-[10px] uppercase tracking-[0.1em] font-bold ml-1 opacity-70">Stub</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Capability strip for the active provider */}
      {providers.find((p) => p.provider === active) && (
        <Card className="border border-gray-200 rounded-2xl p-4" data-testid="broker-capabilities">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[10px] uppercase tracking-[0.1em] font-bold text-gray-500 mr-2">Capabilities:</p>
            {Object.entries(providers.find((p) => p.provider === active).capabilities).map(([cap, on]) => (
              <Badge
                key={cap}
                data-testid={`cap-${cap}`}
                className={`text-[10px] ${on ? "bg-gain text-white" : "bg-gray-100 text-gray-500"}`}
              >
                {CAP_LABELS[cap] || cap}: {on ? "yes" : "no"}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      {/* Panel */}
      <div className="animate-fade-in">
        {active === "alpaca" ? <AlpacaPanel /> : active === "tradestation" ? <TradeStationPanel /> : <IBKRPanel />}
      </div>
    </div>
  );
}
